/* ============================================
   QRScanner — Camera-based QR scanner for PWA
   Uses native BarcodeDetector API (Chrome Android)
   ============================================ */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

declare global {
  interface Window {
    BarcodeDetector?: new (options?: { formats: string[] }) => {
      detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>>
    }
  }
}

interface QRScannerProps {
  isOpen: boolean
  onClose: () => void
}

export default function QRScanner({ isOpen, onClose }: QRScannerProps) {
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [hasDetector, setHasDetector] = useState(true)
  const [cameraError, setCameraError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    // Check for BarcodeDetector support
    if (!window.BarcodeDetector) {
      setHasDetector(false)
      return
    }

    let cancelled = false

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        })

        if (cancelled) {
          stream.getTracks().forEach(t => t.stop())
          return
        }

        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
        }

        // Start scanning
        const detector = new window.BarcodeDetector!({ formats: ['qr_code'] })

        scanIntervalRef.current = setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) return

          try {
            const barcodes = await detector.detect(videoRef.current)
            if (barcodes.length > 0) {
              const value = barcodes[0].rawValue
              // Check if it's a QR login approval URL
              const match = value.match(/\/qr-login\/approve\/([a-f0-9-]+)/i)
              if (match) {
                cleanup()
                onClose()
                navigate(`/qr-login/approve/${match[1]}`)
              }
            }
          } catch {
            // Ignore detection errors
          }
        }, 500)

      } catch (err: any) {
        if (!cancelled) {
          setCameraError(
            err?.name === 'NotAllowedError'
              ? 'Permiso de cámara denegado. Habilita el acceso en la configuración.'
              : 'No se pudo acceder a la cámara.'
          )
        }
      }
    }

    function cleanup() {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current)
        scanIntervalRef.current = null
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
    }

    startCamera()

    return () => {
      cancelled = true
      cleanup()
    }
  }, [isOpen, navigate, onClose])

  if (!isOpen) return null

  return (
    <div className="qr-scanner-overlay" id="qr-scanner-overlay">
      <div className="qr-scanner-header">
        <h2>Escanear QR</h2>
        <button type="button" className="qr-scanner-close" onClick={onClose} id="qr-scanner-close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {!hasDetector ? (
        <div className="qr-scanner-fallback" id="qr-scanner-fallback">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="8" height="8" rx="1" /><rect x="14" y="2" width="8" height="8" rx="1" /><rect x="2" y="14" width="8" height="8" rx="1" /><rect x="14" y="14" width="4" height="4" />
          </svg>
          <p>Tu dispositivo no soporta escaneo de QR nativo.<br />Usa la cámara de tu celular para escanear el código QR directamente.</p>
          <button type="button" className="qr-login-refresh-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>
      ) : cameraError ? (
        <div className="qr-scanner-fallback" id="qr-scanner-camera-error">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 1l22 22" /><path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h6" /><circle cx="12" cy="13" r="4" />
          </svg>
          <p>{cameraError}</p>
          <button type="button" className="qr-login-refresh-btn" onClick={onClose}>
            Cerrar
          </button>
        </div>
      ) : (
        <>
          <div className="qr-scanner-viewport">
            <video ref={videoRef} playsInline muted />
            <div className="qr-scanner-frame" />
          </div>
          <div className="qr-scanner-hint">
            <p>Apunta la cámara al código QR en la pantalla de login</p>
          </div>
        </>
      )}
    </div>
  )
}
